// ─────────────────────────────────────────────────────────────────────────────
// Per-unit CBM anomaly data (prototype 04).
//
// Derived from the unit profile so each unit tells a different story:
// wear-driven units degrade gradually, misuse-driven units spike.
// ─────────────────────────────────────────────────────────────────────────────

import { profileFor } from './fleet';

export const ANOMALY_DOOR_IDS = ['A1L', 'A2L', 'B1L', 'B2L', 'C1L', 'C2L', 'D1L', 'D2L'];
export const WEEKS = ['W14', 'W15', 'W16', 'W17', 'W18', 'W19', 'W20', 'W21'];

export interface CycleEntry {
  id: string;
  week: string;
  type: 'normal' | 'anomalous' | 'borderline';
  cycleClass: string;
  scores: number[]; // [IF, LOF, SVM, AE, Ensemble]
}

function rng(n: number) { return Math.abs(Math.sin(n * 9301 + 49297) * 233280) % 1; }

/** Doors on this unit that the profile flags, restricted to the 8 shown here. */
function watchList(unitId: string): string[] {
  return Object.keys(profileFor(unitId).watchDoors).filter(d => ANOMALY_DOOR_IDS.includes(d));
}

/** % of HIGH-anomaly cycles, per door per week. */
export function getHeatmap(unitId: string): number[][] {
  const p = profileFor(unitId);
  const watched = watchList(unitId);

  return ANOMALY_DOOR_IDS.map((door, di) =>
    WEEKS.map((_, wi) => {
      let base = 5 + rng(p.seedOffset + di * 100 + wi * 7) * 12;

      if (watched.includes(door)) {
        const health = p.watchDoors[door];
        const severity = (85 - health) / 8;
        if (p.misuseScale > 1.5) {
          // Misuse: sharp, episodic spikes rather than a trend.
          base += wi % 3 === 0 ? severity * 9 : severity * 2;
        } else {
          // Wear: steady climb from mid-window.
          base += wi >= 3 ? (wi - 2) * severity * 1.6 : 0;
        }
      }

      base *= 0.8 + p.misuseScale * 0.2;
      return Math.min(95, Math.max(2, Math.round(base)));
    }),
  );
}

/** Representative scored cycles for one door on one unit. */
export function getDoorCycles(unitId: string, doorId: string): CycleEntry[] {
  const p = profileFor(unitId);
  const di = ANOMALY_DOOR_IDS.indexOf(doorId);
  const flagged = p.watchDoors[doorId] !== undefined;
  const health = flagged ? p.watchDoors[doorId] : 90;
  const severity = flagged ? (85 - health) / 45 : 0;   // 0 … ~0.75
  const misuseLed = p.misuseScale > 1.5;

  const templates: Array<{ week: string; base: number; cls: string; type: CycleEntry['type'] }> =
    WEEKS.filter((_, i) => [0, 1, 2, 3, 4, 7].includes(i)).map((week, i) => {
      const ramp = misuseLed
        ? (i === 2 || i === 5 ? 1 : 0.25)          // episodic
        : i / 5;                                    // gradual
      const score = Math.round(9 + severity * 105 * ramp + rng(p.seedOffset + di * 7 + i) * 9);
      const capped = Math.min(94, score);
      const type: CycleEntry['type'] =
        capped >= 60 ? 'anomalous' : capped >= 32 ? 'borderline' : 'normal';
      const cls = capped >= 60
        ? (misuseLed ? 'OBSTRUCTED_CLOSE' : 'MULTI_ATTEMPT')
        : 'FULL_CYCLE';
      return { week, base: capped, cls, type };
    });

  return templates.map((t, ci) => {
    const spread = t.base * 0.25;
    const scores = [0, 1, 2, 3].map(mi => {
      const noise = rng(p.seedOffset + di * 1000 + ci * 100 + mi * 13) * spread - spread / 2;
      return Math.min(99, Math.max(1, Math.round(t.base + noise)));
    });
    scores[4] = Math.round(scores.slice(0, 4).reduce((a, b) => a + b, 0) / 4);
    return {
      id: `${unitId}-${doorId}-${t.week}`,
      week: t.week,
      type: t.type,
      cycleClass: t.cls,
      scores,
    };
  });
}

/** Door with the highest average anomaly rate — the sensible default selection. */
export function worstAnomalyDoor(unitId: string): string {
  const hm = getHeatmap(unitId);
  let best = 0;
  let bestAvg = -1;
  hm.forEach((row, i) => {
    const avg = row.reduce((a, b) => a + b, 0) / row.length;
    if (avg > bestAvg) { bestAvg = avg; best = i; }
  });
  return ANOMALY_DOOR_IDS[best];
}
